import { CheckOutlined, CloseOutlined, DollarOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Space, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatMoney, onModalOk } from "@/shared/lib";

import { useProcessWithdrawal } from "../model/useProcessWithdrawal";

type Dialog = "none" | "paid" | "reject";

/**
 * Кнопки автомата вывода.
 *
 * Доступность считается от текущего статуса, а не от роли: «Выплачено» на
 * неподтверждённой заявке бэкенд отвергнет конфликтом, и предлагать эту кнопку
 * — значит учить оператора игнорировать ошибки.
 *
 * В подтверждении отклонения явно сказано, вернутся деньги или нет: из
 * `approved` они списаны и вернутся, из `pending` — не списывались.
 */
export function WithdrawalActions({
  id,
  userId,
  amount,
  status,
}: {
  id: number;
  userId: number;
  amount: string;
  status: string;
}) {
  const { t } = useTranslation();
  const { approve, markPaid, reject } = useProcessWithdrawal();
  const [dialog, setDialog] = useState<Dialog>("none");
  const [paidForm] = Form.useForm<{ tx_hash: string }>();
  const [rejectForm] = Form.useForm<{ comment: string }>();
  const [modal, contextHolder] = Modal.useModal();

  const money = formatMoney(amount);
  const closed = status === "rejected" || status === "paid";

  const confirmApprove = () => {
    void modal.confirm({
      title: t("withdrawals.approve"),
      content: t("withdrawals.approveConfirm", { amount: money }),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => approve.mutateAsync({ id, userId }),
    });
  };

  return (
    <Space>
      {contextHolder}
      {status === "pending" && (
        <Button
          type="primary"
          icon={<CheckOutlined aria-hidden />}
          loading={approve.isPending}
          onClick={confirmApprove}
        >
          {t("common.confirm")}
        </Button>
      )}
      {status === "approved" && (
        <Button
          type="primary"
          icon={<DollarOutlined aria-hidden />}
          onClick={() => {
            setDialog("paid");
          }}
        >
          {t("withdrawals.paid")}
        </Button>
      )}
      <Button
        danger
        icon={<CloseOutlined aria-hidden />}
        disabled={closed}
        onClick={() => {
          setDialog("reject");
        }}
      >
        {t("common.reject")}
      </Button>

      <Modal
        open={dialog === "paid"}
        title={t("withdrawals.paidTitle")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        okButtonProps={{ loading: markPaid.isPending }}
        onCancel={() => {
          setDialog("none");
        }}
        onOk={onModalOk(paidForm, async (values) => {
          await markPaid.mutateAsync({ id, userId, tx_hash: values.tx_hash });
          paidForm.resetFields();
          setDialog("none");
        })}
      >
        <Typography.Paragraph type="secondary">{t("withdrawals.paidHint")}</Typography.Paragraph>
        <Form form={paidForm} layout="vertical">
          <Form.Item
            name="tx_hash"
            label={t("withdrawals.txHash")}
            rules={[{ required: true, min: 4, message: t("login.required") }]}
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={dialog === "reject"}
        title={t("withdrawals.rejectTitle")}
        okText={t("common.reject")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true, loading: reject.isPending }}
        onCancel={() => {
          setDialog("none");
        }}
        onOk={onModalOk(rejectForm, async (values) => {
          await reject.mutateAsync({ id, userId, comment: values.comment });
          rejectForm.resetFields();
          setDialog("none");
        })}
      >
        <Typography.Paragraph type={status === "approved" ? "warning" : "secondary"}>
          {status === "approved"
            ? t("withdrawals.rejectRefundHint", { amount: money })
            : t("withdrawals.rejectNoRefundHint")}
        </Typography.Paragraph>
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="comment"
            label={t("common.comment")}
            rules={[{ required: true, min: 3, message: t("common.commentRequired") }]}
          >
            <Input.TextArea rows={3} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
